import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  STATUS_CELL_CLASSES,
  WEEK_COLUMNS,
  type DisplayCell,
  type DisplayEventRow,
  type DisplayGroupRow,
  type DisplaySetDateRow,
  type ProjectionTableData,
  type WeekKey,
} from './pogOnTimeProjectionMockData'

interface Props {
  data: ProjectionTableData
}

const LABEL_COL_WIDTH = 170

function Chevron({ expanded }: { expanded: boolean }) {
  return expanded ? (
    <ChevronDown className="inline h-3 w-3 shrink-0" />
  ) : (
    <ChevronRight className="inline h-3 w-3 shrink-0" />
  )
}

function WeekCells({ cells }: { cells: Record<WeekKey, DisplayCell> }) {
  return (
    <>
      {WEEK_COLUMNS.map(col => {
        const cell = cells[col.key]
        const isBySet = col.key === 'bySet'
        return (
          <td
            key={col.key}
            className={cn(
              'border-b border-border px-3 py-1.5 text-right text-xs tabular-nums',
              isBySet && STATUS_CELL_CLASSES[cell.color]
            )}
          >
            {cell.pct}%
          </td>
        )
      })}
    </>
  )
}

export function POGOnTimeProjectionTable({ data }: Props) {
  const [collapsedSetDates, setCollapsedSetDates] = useState<Set<string>>(
    new Set()
  )
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set())

  const toggle = (
    set: Set<string>,
    setter: (s: Set<string>) => void,
    key: string
  ) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setter(next)
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
      <div className="max-h-[640px] overflow-auto">
        <table
          className="w-full border-separate text-xs"
          style={{ borderSpacing: 0 }}
        >
          <thead>
            <tr>
              <th
                style={{
                  width: LABEL_COL_WIDTH,
                  minWidth: LABEL_COL_WIDTH,
                  left: 0,
                }}
                className="sticky top-0 z-20 border-r border-b border-border bg-white px-3 py-2 text-left text-xs font-semibold text-foreground"
              >
                Set Date
              </th>
              <th
                style={{ left: LABEL_COL_WIDTH }}
                className="sticky top-0 z-20 border-r border-b border-border bg-white px-3 py-2 text-left text-xs font-semibold text-foreground"
              >
                POG Group
              </th>
              <th className="sticky top-0 z-10 border-r border-b border-border bg-white px-3 py-2 text-left text-xs font-semibold text-foreground">
                Event
              </th>
              <th className="sticky top-0 z-10 border-r border-b border-border bg-white px-3 py-2 text-left text-xs font-semibold text-foreground">
                DC
              </th>
              {WEEK_COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="sticky top-0 z-10 border-b border-border bg-white px-3 py-2 text-right text-xs font-semibold text-foreground"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                colSpan={4}
                className="border-b border-border bg-slate-50 px-3 py-1.5 font-semibold"
              >
                Total
              </td>
              <WeekCells cells={data.grand} />
            </tr>
            {data.setDates.map(sd => (
              <SetDateBlock
                key={sd.id}
                setDate={sd}
                collapsed={collapsedSetDates.has(sd.id)}
                onToggle={() =>
                  toggle(collapsedSetDates, setCollapsedSetDates, sd.id)
                }
                collapsedGroups={collapsedGroups}
                onToggleGroup={key =>
                  toggle(collapsedGroups, setCollapsedGroups, key)
                }
                expandedEvents={expandedEvents}
                onToggleEventExpanded={key =>
                  toggle(expandedEvents, setExpandedEvents, key)
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SetDateBlock({
  setDate,
  collapsed,
  onToggle,
  collapsedGroups,
  onToggleGroup,
  expandedEvents,
  onToggleEventExpanded,
}: {
  setDate: DisplaySetDateRow
  collapsed: boolean
  onToggle: () => void
  collapsedGroups: Set<string>
  onToggleGroup: (key: string) => void
  expandedEvents: Set<string>
  onToggleEventExpanded: (key: string) => void
}) {
  return (
    <>
      <tr
        className="cursor-pointer bg-slate-50 hover:brightness-95"
        onClick={onToggle}
      >
        <td
          style={{ width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH, left: 0 }}
          className="sticky z-10 border-r border-b border-border bg-slate-50 px-3 py-1.5 font-semibold"
        >
          <span className="inline-flex items-center gap-1">
            <Chevron expanded={!collapsed} />
            {setDate.setDateLabel}
          </span>
        </td>
        <td
          style={{ left: LABEL_COL_WIDTH }}
          className="sticky z-10 border-r border-b border-border bg-slate-50 px-3 py-1.5 font-semibold"
        >
          Total
        </td>
        <td className="border-r border-b border-border bg-slate-50 px-3 py-1.5" />
        <td className="border-r border-b border-border bg-slate-50 px-3 py-1.5" />
        <WeekCells cells={setDate.cells} />
      </tr>
      {!collapsed &&
        setDate.groups.map(group => (
          <GroupBlock
            key={group.id}
            group={group}
            collapsed={collapsedGroups.has(group.id)}
            onToggle={() => onToggleGroup(group.id)}
            expandedEvents={expandedEvents}
            onToggleEventExpanded={onToggleEventExpanded}
          />
        ))}
    </>
  )
}

function GroupBlock({
  group,
  collapsed,
  onToggle,
  expandedEvents,
  onToggleEventExpanded,
}: {
  group: DisplayGroupRow
  collapsed: boolean
  onToggle: () => void
  expandedEvents: Set<string>
  onToggleEventExpanded: (key: string) => void
}) {
  return (
    <>
      <tr
        className="cursor-pointer bg-white hover:bg-muted/30"
        onClick={onToggle}
      >
        <td
          style={{ width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH, left: 0 }}
          className="sticky z-10 border-r border-b border-border bg-white px-3 py-1.5"
        />
        <td
          style={{ left: LABEL_COL_WIDTH }}
          className="sticky z-10 border-r border-b border-border bg-white px-3 py-1.5 font-medium"
        >
          <span className="inline-flex items-center gap-1">
            <Chevron expanded={!collapsed} />
            {group.pogGroup}
          </span>
        </td>
        <td className="border-r border-b border-border bg-white px-3 py-1.5 text-muted-foreground">
          Total
        </td>
        <td className="border-r border-b border-border bg-white px-3 py-1.5" />
        <WeekCells cells={group.cells} />
      </tr>
      {!collapsed &&
        group.events.map(event => (
          <EventBlock
            key={event.id}
            event={event}
            collapsed={!expandedEvents.has(event.id)}
            onToggle={() => onToggleEventExpanded(event.id)}
          />
        ))}
    </>
  )
}

function EventBlock({
  event,
  collapsed,
  onToggle,
}: {
  event: DisplayEventRow
  collapsed: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr
        className="cursor-pointer bg-white hover:bg-muted/20"
        onClick={onToggle}
      >
        <td
          style={{ width: LABEL_COL_WIDTH, minWidth: LABEL_COL_WIDTH, left: 0 }}
          className="sticky z-10 border-r border-b border-border bg-white px-3 py-1.5"
        />
        <td
          style={{ left: LABEL_COL_WIDTH }}
          className="sticky z-10 border-r border-b border-border bg-white px-3 py-1.5"
        />
        <td className="border-r border-b border-border bg-white px-3 py-1.5 text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Chevron expanded={!collapsed} />
            {event.event}
          </span>
        </td>
        <td className="border-r border-b border-border bg-white px-3 py-1.5" />
        <WeekCells cells={event.cells} />
      </tr>
      {!collapsed &&
        event.dcRows.map((dc, i) => (
          <tr
            key={`${event.id}-${dc.dc}-${i}`}
            className="bg-white hover:bg-muted/10"
          >
            <td
              style={{
                width: LABEL_COL_WIDTH,
                minWidth: LABEL_COL_WIDTH,
                left: 0,
              }}
              className="sticky z-10 border-r border-b border-border bg-white px-3 py-1.5"
            />
            <td
              style={{ left: LABEL_COL_WIDTH }}
              className="sticky z-10 border-r border-b border-border bg-white px-3 py-1.5"
            />
            <td className="border-r border-b border-border bg-white px-3 py-1.5" />
            <td className="border-r border-b border-border bg-white px-3 py-1.5 text-muted-foreground">
              {formatDistributionCenterName(dc.dc)}
            </td>
            <WeekCells cells={dc.cells} />
          </tr>
        ))}
    </>
  )
}
